import { useEffect } from 'react'
import moment from 'moment-timezone'
import { split, length } from 'ramda'
import { useTranslation } from 'react-i18next'
import cn from 'classnames/bind'

import useFromTo from 'app/common/hooks/useFromTo'
import toFixedValue from 'app/common/lib/toFixedValue'
import { useCurrencySign } from 'app/common/components-next/currency'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent } from 'app/common/templates-next/page-template'

import FinanceHistoryTable from './FinanceHistoryTable'
import AccountBalanceData, { useBalance, useBalanceHistory } from '../AccountBalanceData'
import styles from './AccountFinancesHistory.module.scss';

const cx = cn.bind(styles)

type Props = {
  date: string
  timezone: string
  headerCommentary?: string
  bannerPlaceId?: string
}

const AccountFinancesHistory = ({ date, timezone, headerCommentary, bannerPlaceId }: Props) => {
  const { t } = useTranslation('finances')
  const dateType = length(split('-', date)) // 2 - мясяцы, 3 - дни
  const [from, to] = useFromTo({ date, dateType })
  const dateString = moment(date).format('MMMM YYYY').toLowerCase()

  const [balance, requestBalance, loadingBalance] = useBalance()
  const [balanceHistory, requestBalanceHistory, loadingBalanceHistory] = useBalanceHistory()
  const balanceString = toFixedValue(balance.primary_balance || 0)

  useEffect(() => {
    requestBalance()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    requestBalanceHistory(from, to)
  }, [from, to]) // eslint-disable-line react-hooks/exhaustive-deps

  const currency = useCurrencySign('spaceBefore')

  return (
    <AccountBalanceData>
      <Sticky>
        {(ref, sticky) => (
          <PageTemplateContent reference={ref} className={cx({ sticky })}>
            <div className={styles.header}>
              <span>{t('sub_header1', { date: dateString })}</span>
              <span>
                {t('sub_header2', { balance: loadingBalance ? '...' : balanceString })}
                {currency}
              </span>
            </div>
            {headerCommentary && <div className={styles.headerCommentary}>{headerCommentary}</div>}
          </PageTemplateContent>
        )}
      </Sticky>

      <FinanceHistoryTable
        timezone={timezone}
        date={date}
        bannerPlaceId={bannerPlaceId}
        data={balanceHistory}
        loading={loadingBalanceHistory}
      />
    </AccountBalanceData>
  )
}

export default AccountFinancesHistory
